/**
 * Created by Tiger on 10/06/2022.
 */
/** @jsx jsx */
import {jsx, css} from '@emotion/react'
import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {Column} from "./lib";
import styled from "@emotion/styled";
import {Button, Input, InputRef, message, Row, Spin, Typography} from "antd";
import useInterval from "../../../hooks/useInterval";
import {useHistory, useRouteMatch} from "react-router-dom";
import {ContextProps, LoginType, MobileLoginContext} from "./index";
import {getStrQueryVariable} from "../../../common/util";
import i18n from "i18next";
import {LoadingOutlined} from "@ant-design/icons";
import {getErrorDescription} from "./Password";
import Back from "./Back";

/**验证码的长度 */
const CODE_LENGTH: number = 6;
const defaultTimeout: number = 60;


interface CodeProps {
  account: string;
  loginType: LoginType;
  countryCode?: string;
}

const VerificationCode = () => {
  const [code, setCode] = useState('');
  const textInput = useRef<InputRef>(null)
  const [codeTimeout, setCodeTimeout] = useState(defaultTimeout);

  const {params} = useRouteMatch()
  const history = useHistory()
  const {account, countryCode, loginType} = params as CodeProps
  const tip = i18n.t('code_sent_tip').replace('%@', loginType === 'Phone' ? `+${countryCode} ${account}` : `${account}`)
  const context = useContext(MobileLoginContext);
  const {cid = '', loginWithCode, getCode, updateAuth} = context as ContextProps
  const [refreshCode, setRefreshCode] = useState(false);
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const alertInfo = useCallback(
    (error?: string | undefined) => {
      message.error(error || i18n.t('set_code_fail'))
      setCodeTimeout(0)
    },
    [],
  );


  useEffect(() => {
    setCodeTimeout(0)
    setLoading(true)
    getCode(account, countryCode || '')?.then(res => {
      console.log('getCode:', res)
      if (res.data.code !== 0) {
        setCodeTimeout(0)
        alertInfo()
      } else {
        setToken(res.data.data.t)
        setCodeTimeout(defaultTimeout)
      }
    }).catch(error => {
      alertInfo(i18n.t('set_code_fail'))
    }).finally(() => {
      setLoading(false)
    })
  }, [refreshCode])

  const login = (code: string) => {
    setLoading(true)
    loginWithCode({account, code, cid, country_code: countryCode, t: token})
      ?.then(res => {
        if (!!cid && res?.data?.data?.redirect) {
          updateAuth({
            redirectUrl: res.data.data.redirect.split('?')[0],
            code: getStrQueryVariable('code', res.data.data.redirect),
            access_key: getStrQueryVariable('access_key', res.data.data.redirect) || '',
            cid: cid,
            nonce: getStrQueryVariable('nonce', res.data.data.redirect)
          })
        } else {
          const {code = -2000} = res?.data || {}
          alertInfo(getErrorDescription(code))
        }
      }).catch(error => {
      alertInfo(i18n.t('login_fail'))
    }).finally(() => {
      setLoading(false)
    })
  }

  useInterval(() => {
    setCodeTimeout(oldTimeout => oldTimeout - 1)
  }, codeTimeout > 0 ? 1000 : null)

  const updateCode = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = event.target
    const reg = /^[0-9]*$/;
    if (reg.test(value)) {
      setCode(value);
      if (value.length === CODE_LENGTH) {

        login(value);
      }
    }
  };

  const icon = <LoadingOutlined style={{fontSize: 30}}/>

  return (
    <Spin size={'large'} indicator={icon} spinning={loading}>
      <Column onClick={() => textInput.current?.focus()}>
        <Back title={i18n.t('via_verify')} onClick={() => {history.goBack()}}/>
        <Text>{tip}</Text>
        {loginType === 'Email' && <GrayText>{i18n.t('code_send_tip2')}</GrayText>}
        <div css={css`display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%`}>
          <Row align={'middle'} justify={"center"}>
            {
              code.split('').map((code, index) => (
                <Code key={`code-${index}`} code={code}/>
              ))
            }
            {
              Array(CODE_LENGTH - code.length)
                .fill('-')
                .map((_, index) => (
                  <Placeholder key={`placeholder-${index}`} type={index === 0 ? 'blue' : 'gray'}/>
                ))
            }
          </Row>
          {codeTimeout > 0 && <Typography.Text style={{marginTop: 30}}
          >{i18n.t('send_code_timeout').replace('%@', `${codeTimeout}`)}</Typography.Text>}
          {codeTimeout <= 0 && <Typography.Text onClick={() => {
            setRefreshCode(oldFlag => !oldFlag)
          }} style={{marginTop: 30, color: '#007AFF'}}>{i18n.t('re_send_code')}</Typography.Text>}
        </div>

        <MyInput
          ref={textInput}
          maxLength={CODE_LENGTH}
          autoFocus
          inputMode={"numeric"}
          onChange={updateCode}
          value={code}
        />
      </Column>

    </Spin>
  )
}

const Code = ({code}: { code: string }) => {
  return <CodeContainer>
    <CodeText>{code}</CodeText>
  </CodeContainer>
}

type PlaceholderType = 'blue' | 'gray';
const Placeholder = ({type}: { type: PlaceholderType }) => {
  return <CodeContainer>
    <PlaceholderText type={type}/>
  </CodeContainer>
}


const CodeContainer = styled.div`
  margin-top: 30px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 60px;
  width: 45px;
`

const CodeText = styled(Typography.Text)`
  font-size: 50px;
  color: #007AFF;
`

const PlaceholderText = styled.div<{ type: PlaceholderType }>`
  width: 15px;
  height: 4px;
  background: ${props => props.type === 'gray' ? '#000000' : ' #007AFF'};
  opacity: ${props => props.type === 'gray' ? .3 : 1};
`

const Text = styled(Typography.Text)`
  margin-top: 80px;
  color: black;
`

const GrayText = styled(Text)`
  opacity: .5;
  margin-top: 15px;
`

const MyInput = styled(Input)`
  opacity: 0;
  width: 100%;
  height: 1px;
  position: absolute;
  left: 0;
  bottom: 0;
  background: transparent;;
`

export default VerificationCode

VerificationCode.whyDidYouRender = true
